import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Tooltip, Filler
} from 'chart.js';
import { fmt, tradeValue, insuranceValue, pct, MONTHS } from '../data';
import ValueBadges from '../components/ValueBadges';
import { useWatchPrice } from '../hooks/useWatchPrice';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

function getSourceLabel(source, sampleSize) {
  if (!source || source === 'static' || source === 'static_fallback' || source === 'error_fallback' || source === 'mock') {
    return { label: '● Curated estimate · May 2026', color: '#9898aa' };
  }
  const parts = source.split('+').map(s => {
    if (s === 'ebay-sold')    return 'eBay Sold';
    if (s === 'chrono24')     return 'Chrono24';
    if (s === 'watchcharts')  return 'WatchCharts';
    if (s === 'reddit-sold')  return 'Reddit Sold';
    if (s === 'reddit')       return 'Reddit';
    if (s === 'ebay')         return 'eBay';
    return s;
  });
  const hasSold = source.includes('sold') || source.includes('ebay');
  const label = hasSold
    ? `● Sold listings · ${parts.join(' + ')}${sampleSize ? ` · ${sampleSize} sales` : ''}`
    : `● Live · ${parts.join(' + ')}${sampleSize ? ` · ${sampleSize} listings` : ''}`;
  return { label, color: '#0f9b6e' };
}

function InfoRow({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      padding: '10px 0', borderBottom: '0.5px solid var(--border)',
      flexWrap: 'wrap', gap: 4,
    }}>
      <span style={{ fontSize: 13, color: 'var(--text2)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

export default function DetailPage({ watch, onBack }) {
  const { price, loading, source, sampleSize } = useWatchPrice(watch);
  const trade   = tradeValue(price);
  const insure  = insuranceValue(price);
  const src = getSourceLabel(source, sampleSize);

  const chartData = {
    labels: MONTHS,
    datasets: [{
      label: 'Market Price',
      data: watch.history,
      borderColor: '#185FA5',
      backgroundColor: 'rgba(24,95,165,0.07)',
      borderWidth: 2, tension: 0.35,
      pointRadius: 0, pointHoverRadius: 5,
      fill: true,
    }],
  };

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => ' ' + fmt(Math.round(ctx.parsed.y)) } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: '#9a9890' },
      },
      y: {
        grid: { color: 'rgba(128,128,128,0.08)' },
        ticks: {
          font: { size: 11 }, color: '#9a9890',
          callback: v => '$' + (v >= 1000 ? Math.round(v / 1000) + 'k' : v),
        },
      },
    },
    interaction: { mode: 'index', intersect: false },
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px', position: 'relative', zIndex: 1 }}>

      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 13, color: 'var(--text2)',
          border: 'none', background: 'none',
          cursor: 'pointer', marginBottom: 20, padding: 0,
          fontFamily: "'Space Grotesk', sans-serif",
        }}
      >
        <i className="ti ti-arrow-left" /> Back to market
      </button>

      {/* Responsive two-column grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16, alignItems: 'start',
      }}>

        {/* ── LEFT COLUMN ── */}
        <div>

          {/* Hero card */}
          <div style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 24, marginBottom: 16,
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', marginBottom: 8,
              gap: 8, flexWrap: 'wrap',
            }}>
              <div style={{
                fontSize: 10, color: 'var(--text3)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                fontFamily: "'Space Mono', monospace",
              }}>
                {watch.brand}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: loading ? 'var(--text3)' : src.color,
                fontFamily: "'Space Mono', monospace",
              }}>
                {loading ? '● Fetching live price…' : src.label}
              </div>
            </div>

            <h1 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(18px,4vw,24px)',
              fontWeight: 700, marginBottom: 6,
              letterSpacing: '-0.01em',
            }}>
              {watch.model}
            </h1>

            {watch.nickname && (
              <div style={{ marginBottom: 8 }}>
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  color: '#C0392B',
                  background: 'rgba(192,57,43,0.08)',
                  padding: '3px 10px', borderRadius: 6,
                  fontFamily: "'Space Grotesk', sans-serif",
                }}>
                  "{watch.nickname}"
                </span>
              </div>
            )}

            {watch.colorway && (
              <div style={{
                fontSize: 13, color: 'var(--text2)',
                lineHeight: 1.6, marginBottom: 14,
                fontFamily: "'Space Grotesk', sans-serif",
              }}>
                {watch.colorway}
              </div>
            )}

            <div style={{
              fontSize: 11, color: 'var(--text3)', marginBottom: 20,
              fontFamily: "'Space Mono', monospace", letterSpacing: '0.04em',
            }}>
              Ref. {watch.ref} · {watch.year} · {watch.condition}
            </div>

            <ValueBadges market={price} size="lg" />
          </div>

          {/* Price chart */}
          <div style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 24,
          }}>
            <div style={{
              fontSize: 14, fontWeight: 600, marginBottom: 4,
              fontFamily: "'Space Grotesk', sans-serif",
            }}>
              12-month market price
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              Secondary market trend
            </div>
            <div style={{ position: 'relative', height: 200 }}>
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

        </div>

        {/* ── RIGHT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Valuation breakdown */}
          <div style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 20,
          }}>
            <div style={{
              fontSize: 14, fontWeight: 600, marginBottom: 14,
              fontFamily: "'Space Grotesk', sans-serif",
            }}>
              Valuation breakdown
            </div>

            {[
              {
                label: 'Market Price',
                value: loading ? '…' : fmt(price),
                color: 'var(--market)',
                bg: 'rgba(24,95,165,0.07)',
                desc: 'Current secondary market — what buyers pay today',
              },
              {
                label: 'Trade Value',
                value: loading ? '…' : fmt(trade),
                color: 'var(--trade)',
                bg: 'rgba(60,52,137,0.07)',
                desc: 'Estimated dealer offer (70% of market)',
              },
              {
                label: 'Insurance Payout',
                value: loading ? '…' : fmt(insure),
                color: 'var(--insure)',
                bg: 'rgba(15,110,86,0.07)',
                desc: 'Recommended coverage (125% of market)',
              },
            ].map(v => (
              <div key={v.label} style={{
                background: v.bg, borderRadius: 8,
                padding: '14px 16px', marginBottom: 10,
              }}>
                <div style={{
                  fontSize: 10, color: v.color, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  marginBottom: 4, fontFamily: "'Space Mono', monospace",
                }}>
                  {v.label}
                </div>
                <div style={{
                  fontSize: 'clamp(18px,5vw,24px)', fontWeight: 700,
                  color: v.color, fontFamily: "'Space Grotesk', sans-serif",
                  marginBottom: 4,
                }}>
                  {v.value}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {v.desc}
                </div>
              </div>
            ))}
          </div>

          {/* Watch details */}
          <div style={{
            background: 'var(--surface)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 20,
          }}>
            <div style={{
              fontSize: 14, fontWeight: 600, marginBottom: 12,
              fontFamily: "'Space Grotesk', sans-serif",
            }}>
              Watch details
            </div>
            <InfoRow label="Original MSRP"    value={fmt(watch.msrp)} />
            <InfoRow label="Market premium"   value={pct(watch.market, watch.msrp) + ' above retail'} />
            <InfoRow label="Year"             value={watch.year} />
            <InfoRow label="Condition"        value={watch.condition} />
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 12 }}>
              {watch.tags.map(t => (
                <span key={t} style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 99,
                  border: '0.5px solid var(--border2)',
                  color: 'var(--text2)',
                  fontFamily: "'Space Grotesk', sans-serif",
                }}>
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{
            background: 'var(--surface2)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 14,
          }}>
            <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
              <i className="ti ti-info-circle" style={{ marginRight: 4 }} />
              {(source && source !== 'static' && source !== 'curated')
                ? `Market price based on recent sold listings from ${source.split('+').map(s => s === 'ebay-sold' ? 'eBay' : s === 'reddit-sold' ? 'Reddit WatchExchange' : s).join(', ')}. Trade and insurance values are calculated estimates.`
                : 'Valuations are curated estimates based on May 2026 secondary market research. Consult a certified appraiser for official documentation.'}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
